import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import {
  Pencil,
  Trash2,
  Plus,
  Search,
  Wallet,
  PieChart as PieIcon,
  TrendingUp,
  Layers,
  AlertTriangle,
  CheckCircle2,
  Sparkles,
  Lightbulb,
  ArrowUpDown,
  Wand2,
  Info,
  Calculator,
} from "lucide-react";
import {
  v2CreateDerivedVariable,
  v2ListComponents,
  v2ListDerivedVariables,
  v2UpdateDerivedVariable,
  v2ValidateFormula,
} from "./SalaryApiV2";
import { entGetCatalog } from "./salaryEnterpriseApi";
import ConfirmDialog from "../../../components/ui/ConfirmDialog";
import { useToast } from "../../Context/ToastContext";
import { evaluateAll, evaluateExpression, referencedIdentifiers } from "./derivedFormulaEval";
import "../../../styles/derived-formulas-premium.css";
import "../../../styles/salary-components-premium.css";
import {
  applyFormulaToComponentAndReturn,
  componentEditPath,
  parseComponentToolSearchParams,
} from "./enterprise/components/componentFormulaNav";

function toErr(e, fallback) {
  return e?.detail || e?.message || fallback;
}

function emptyForm() {
  return { code: "", name: "", expression: "" };
}

function rowToForm(row) {
  return {
    code: row?.code || "",
    name: row?.name || "",
    expression: row?.expression || "",
  };
}

function buildKnownIdentifiers({ catalog, components, derivedRows, formCode }) {
  const ids = new Set(catalog?.formula_variables || []);
  for (const row of components || []) {
    if (row?.code) ids.add(String(row.code).trim());
  }
  for (const row of derivedRows || []) {
    if (row?.code) ids.add(String(row.code).trim());
  }
  const self = String(formCode || "").trim().toUpperCase();
  if (self) ids.delete(self);
  return [...ids].sort();
}

/* ── allocation model (presentation-only) ──────────────────── */
const BASE_VARS = new Set(["MONTHLY_CTC", "ANNUAL_CTC", "CTC", "GROSS"]);
const STATUTORY_RE = /(^|_)(PF|EPF|VPF|ESI|ESIC|PT|TDS|NPS|LWF|GRATUITY|PROF)(_|$)/i;
const PALETTE = ["#2563eb", "#7c3aed", "#0d9488", "#f59e0b", "#ec4899", "#06b6d4", "#84cc16", "#f97316", "#6366f1", "#14b8a6"];

const INR = new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 });
function money(n) {
  if (!Number.isFinite(n)) return "—";
  return INR.format(Math.round(n));
}
function pct1(n) {
  if (!Number.isFinite(n)) return "0%";
  return `${(Math.round(n * 10) / 10).toString()}%`;
}

function compCategory(c) {
  return c?.meta?.payroll_display_category || c?.component_category || "";
}

/** Standard Indian salary structure — shown first in suggested allowances. */
const STANDARD_INDIAN_SALARY_PRESETS = [
  { code: "MONTHLY_CTC", name: "Monthly CTC", expr: "ANNUAL_CTC / 12", note: "Annual CTC ÷ 12", standard: true },
  { code: "BASIC", name: "Basic", expr: "MONTHLY_CTC * 0.40", note: "40% of Monthly CTC", standard: true },
  { code: "HRA", name: "House Rent Allowance", expr: "BASIC * 0.40", note: "40% of Basic", standard: true },
  { code: "PF_EMPLOYEE", name: "Employee PF", expr: "BASIC * 0.12", note: "12% of Basic (employee share)", standard: true },
  { code: "PF_EMPLOYER", name: "Employer PF", expr: "BASIC * 0.12", note: "12% of Basic (employer share)", standard: true },
  { code: "SPA", name: "Special Allowance", expr: "MONTHLY_CTC - BASIC - HRA - PF_EMPLOYER", note: "Remaining CTC balance", standard: true },
];

const OTHER_RECOMMENDATIONS = [
  { code: "SPECIAL_ALLOWANCE", name: "Special Allowance", expr: "MONTHLY_CTC - (BASIC + HRA)", note: "Balances the package" },
  { code: "MEDICAL_ALLOWANCE", name: "Medical Allowance", expr: "MONTHLY_CTC * 5%", note: "~5% of CTC" },
  { code: "TRAVEL_ALLOWANCE", name: "Travel / Conveyance", expr: "MONTHLY_CTC * 5%", note: "~5% of CTC" },
  { code: "PERFORMANCE_ALLOWANCE", name: "Performance Allowance", expr: "MONTHLY_CTC * 10%", note: "~10% of CTC" },
  { code: "FUEL_ALLOWANCE", name: "Fuel Allowance", expr: "MONTHLY_CTC * 3%", note: "~3% of CTC" },
  { code: "INTERNET_ALLOWANCE", name: "Internet Allowance", expr: "MONTHLY_CTC * 2%", note: "~2% of CTC" },
];

function statusFor(pct) {
  if (pct > 100.5) return { key: "over", label: "Over-allocated", tone: "red" };
  if (pct >= 99.5) return { key: "full", label: "Fully allocated", tone: "green" };
  if (pct >= 90) return { key: "near", label: "Nearly full", tone: "orange" };
  return { key: "healthy", label: "Healthy package", tone: "green" };
}

/* ── donut chart ───────────────────────────────────────────── */
function Donut({ segments, remainingPct }) {
  const r = 52;
  const c = 2 * Math.PI * r;
  const [hover, setHover] = useState(null);
  let offset = 0;
  const drawn = [];
  for (const s of segments) {
    const frac = Math.max(0, Math.min(1, s.pct / 100));
    drawn.push({ ...s, dash: frac * c, off: offset });
    offset += frac * c;
  }
  const remFrac = Math.max(0, Math.min(1, remainingPct / 100));
  return (
    <div className="df-donut-wrap">
      <svg viewBox="0 0 140 140" className="df-donut" role="img" aria-label="Component distribution">
        <circle cx="70" cy="70" r={r} fill="none" stroke="var(--slate-100)" strokeWidth="16" />
        {remFrac > 0 ? (
          <circle
            cx="70" cy="70" r={r} fill="none" stroke="var(--slate-200)" strokeWidth="16"
            strokeDasharray={`${remFrac * c} ${c}`} strokeDashoffset={-offset} transform="rotate(-90 70 70)"
          />
        ) : null}
        {drawn.map((s) => (
          <circle
            key={s.code}
            cx="70" cy="70" r={r} fill="none" stroke={s.color}
            strokeWidth={hover === s.code ? 19 : 16}
            strokeDasharray={`${s.dash} ${c}`} strokeDashoffset={-s.off}
            transform="rotate(-90 70 70)"
            style={{ transition: "stroke-width 0.15s ease, stroke-dasharray 0.5s ease" }}
            onMouseEnter={() => setHover(s.code)}
            onMouseLeave={() => setHover(null)}
          />
        ))}
        <text x="70" y="66" textAnchor="middle" className="df-donut-big">{segments.length}</text>
        <text x="70" y="84" textAnchor="middle" className="df-donut-small">earnings</text>
      </svg>
      {hover ? (
        (() => {
          const s = drawn.find((d) => d.code === hover);
          return (
            <div className="df-donut-tip">
              <span className="df-dot" style={{ background: s.color }} />
              <strong>{s.name}</strong> · {pct1(s.pct)} · {money(s.value)}
            </div>
          );
        })()
      ) : null}
    </div>
  );
}

function FormulaFields({ form, setForm, submitted, idPrefix, codeReadOnly = false }) {
  return (
    <div className="df-form-grid">
      <div className="df-field">
        <label className="df-label" htmlFor={`${idPrefix}-code`}>Code</label>
        <input
          id={`${idPrefix}-code`}
          className={`df-input df-mono${submitted && !form.code.trim() ? " invalid" : ""}${codeReadOnly ? " ro" : ""}`}
          value={form.code}
          onChange={(e) => setForm((p) => ({ ...p, code: e.target.value }))}
          placeholder="MONTHLY_CTC"
          readOnly={codeReadOnly}
        />
      </div>
      <div className="df-field">
        <label className="df-label" htmlFor={`${idPrefix}-name`}>Name</label>
        <input
          id={`${idPrefix}-name`}
          className={`df-input${submitted && !form.name.trim() ? " invalid" : ""}`}
          value={form.name}
          onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
          placeholder="Monthly CTC"
        />
      </div>
      <div className="df-field df-col-span">
        <label className="df-label" htmlFor={`${idPrefix}-expression`}>Expression</label>
        <input
          id={`${idPrefix}-expression`}
          className={`df-input df-mono${submitted && !form.expression.trim() ? " invalid" : ""}`}
          value={form.expression}
          onChange={(e) => setForm((p) => ({ ...p, expression: e.target.value }))}
          placeholder="MONTHLY_CTC * 40%"
        />
      </div>
    </div>
  );
}

export default function SalaryV2DerivedVariables() {
  const toast = useToast();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const toolCtx = useMemo(() => parseComponentToolSearchParams(searchParams), [searchParams]);
  const editingComponent = Boolean(toolCtx.componentId);
  const formRef = useRef(null);
  const [rows, setRows] = useState([]);
  const [components, setComponents] = useState([]);
  const [catalog, setCatalog] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [submitted, setSubmitted] = useState(false);

  const [form, setForm] = useState(() => emptyForm());
  const [validating, setValidating] = useState(false);
  const [validation, setValidation] = useState(null);
  const [saving, setSaving] = useState(false);

  const [editing, setEditing] = useState(null);
  const [editForm, setEditForm] = useState(() => emptyForm());
  const [editSubmitted, setEditSubmitted] = useState(false);
  const [editSaving, setEditSaving] = useState(false);
  const [editValidation, setEditValidation] = useState(null);
  const [editValidating, setEditValidating] = useState(false);

  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [addingPresetCode, setAddingPresetCode] = useState("");
  const [addingAllStandard, setAddingAllStandard] = useState(false);

  // dashboard-only UI state
  const [monthlyCTC, setMonthlyCTC] = useState(() => {
    try {
      const v = Number(localStorage.getItem("df_sample_ctc"));
      return v > 0 ? v : 60000;
    } catch {
      return 60000;
    }
  });
  const [tableSearch, setTableSearch] = useState("");
  const [sort, setSort] = useState({ key: "code", dir: "asc" });

  function setCTC(v) {
    const n = Math.max(0, Number(v) || 0);
    setMonthlyCTC(n);
    try {
      localStorage.setItem("df_sample_ctc", String(n));
    } catch {
      /* ignore */
    }
  }

  async function load() {
    try {
      setLoading(true);
      setError("");
      const [data, comps, cat] = await Promise.all([
        v2ListDerivedVariables(),
        v2ListComponents().catch(() => []),
        entGetCatalog().catch(() => null),
      ]);
      setRows(Array.isArray(data) ? data.filter((r) => r.is_active !== false) : []);
      setComponents(Array.isArray(comps) ? comps : []);
      setCatalog(cat);
    } catch (e) {
      setError(toErr(e, "Failed to load derived variables"));
      setRows([]);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
  }, []);

  useEffect(() => {
    const expr = String(location.state?.expression || "").trim();
    if (!expr) return;
    setForm((prev) => ({ ...prev, expression: expr }));
    formRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  }, [location.state?.expression]);

  function applyExpressionForComponent(expression) {
    const expr = String(expression || "").trim();
    if (!expr || !toolCtx.componentId) {
      toast.error("Open this page from a component to apply a formula.");
      return;
    }
    applyFormulaToComponentAndReturn(navigate, {
      componentId: toolCtx.componentId,
      expression: expr,
      returnTo: toolCtx.returnTo || componentEditPath(toolCtx.componentId),
    });
    toast.success("Expression applied — save on the component editor.");
  }

  const knownIdentifiers = useMemo(() => {
    const ids = buildKnownIdentifiers({ catalog, components, derivedRows: rows, formCode: form.code });
    const derivedCodes = (rows || []).map((r) => String(r.code || "").trim()).filter(Boolean);
    const merged = [...new Set([...derivedCodes, ...ids])];
    return merged.sort((a, b) => a.localeCompare(b));
  }, [catalog, components, rows, form.code]);

  const editKnownIdentifiers = useMemo(
    () => buildKnownIdentifiers({ catalog, components, derivedRows: rows, formCode: editing?.code }),
    [catalog, components, rows, editing?.code]
  );

  const displayTokens = useMemo(() => {
    const priority = new Set((rows || []).map((r) => String(r.code || "").trim()).filter(Boolean));
    const head = knownIdentifiers.filter((t) => priority.has(t));
    const tail = knownIdentifiers.filter((t) => !priority.has(t));
    return [...head, ...tail];
  }, [knownIdentifiers, rows]);

  /* ── live allocation computation (frontend-only preview) ── */
  const allocation = useMemo(() => {
    const baseScope = { MONTHLY_CTC: monthlyCTC, ANNUAL_CTC: monthlyCTC * 12, CTC: monthlyCTC * 12 };
    const { scope, resolved } = evaluateAll(rows, baseScope);

    const earningCodeSet = new Set(
      (components || [])
        .filter((c) => ["earning", "benefit"].includes(compCategory(c)))
        .map((c) => String(c.code || "").trim().toUpperCase())
    );
    const isEarningVar = (code) => {
      const u = String(code || "").trim().toUpperCase();
      if (!u || BASE_VARS.has(u)) return false;
      if (earningCodeSet.size) return earningCodeSet.has(u);
      if (STATUTORY_RE.test(u)) return false;
      if (/_EMPLOYER$/.test(u)) return false;
      return true;
    };

    const segments = rows
      .map((r) => ({ code: String(r.code).trim(), name: r.name || r.code, value: resolved[String(r.code).trim()] }))
      .filter((s) => isEarningVar(s.code) && Number.isFinite(s.value) && s.value > 0)
      .sort((a, b) => b.value - a.value)
      .map((s, i) => ({
        ...s,
        pct: monthlyCTC > 0 ? (s.value / monthlyCTC) * 100 : 0,
        color: PALETTE[i % PALETTE.length],
      }));

    const allocatedAmount = segments.reduce((sum, s) => sum + s.value, 0);
    const allocatedPct = monthlyCTC > 0 ? (allocatedAmount / monthlyCTC) * 100 : 0;
    const remainingAmount = monthlyCTC - allocatedAmount;
    const remainingPct = 100 - allocatedPct;

    return { scope, resolved, segments, allocatedAmount, allocatedPct, remainingAmount, remainingPct, status: statusFor(allocatedPct) };
  }, [rows, components, monthlyCTC]);

  /* ── live feedback for the create-form expression ── */
  const liveFeedback = useMemo(() => {
    const expr = form.expression.trim();
    if (!expr) return null;
    const res = evaluateExpression(expr, allocation.scope);
    const refs = referencedIdentifiers(expr).filter((t) => !BASE_VARS.has(t.toUpperCase()) && allocation.resolved[t] != null);
    if (!res.ok) return { ok: false, refs };
    const pct = monthlyCTC > 0 ? (res.value / monthlyCTC) * 100 : 0;
    return { ok: true, value: res.value, pct, refs };
  }, [form.expression, allocation, monthlyCTC]);

  const smartInsights = useMemo(() => {
    const out = [];
    const { allocatedPct, segments, remainingPct } = allocation;
    if (segments.length === 0) {
      out.push({ tone: "info", text: "Add earning variables (BASIC, HRA…) to see your package balance." });
      return out;
    }
    if (allocatedPct > 100.5) out.push({ tone: "bad", text: `Package exceeds Monthly CTC by ${pct1(allocatedPct - 100)}.` });
    else if (allocatedPct >= 99.5) out.push({ tone: "good", text: "Salary package is fully balanced." });
    else out.push({ tone: "good", text: `${pct1(remainingPct)} of Monthly CTC is still available to allocate.` });

    const basic = segments.find((s) => s.code.toUpperCase() === "BASIC");
    if (basic) {
      if (basic.pct > 50) out.push({ tone: "warn", text: `Basic is ${pct1(basic.pct)} of CTC — above the usual 40–50% range.` });
      else out.push({ tone: "good", text: `Basic (${pct1(basic.pct)}) is within the recommended range.` });
    }
    const hra = segments.find((s) => s.code.toUpperCase() === "HRA");
    if (hra) out.push({ tone: "good", text: `HRA is ${pct1(hra.pct)} of CTC.` });
    return out;
  }, [allocation]);

  const canValidate = form.expression.trim().length > 0;
  const canSave = form.code.trim() && form.name.trim() && form.expression.trim();
  const canEditSave = editForm.name.trim() && editForm.expression.trim();
  const overAllocated = allocation.allocatedPct > 100.5;

  const existingCodes = useMemo(
    () => new Set((rows || []).map((r) => String(r.code || "").trim().toUpperCase())),
    [rows]
  );

  const rowsByCode = useMemo(() => {
    const map = new Map();
    for (const row of rows || []) {
      const code = String(row.code || "").trim().toUpperCase();
      if (code) map.set(code, row);
    }
    return map;
  }, [rows]);

  const missingStandardPresets = useMemo(
    () => STANDARD_INDIAN_SALARY_PRESETS.filter((rec) => !existingCodes.has(rec.code)),
    [existingCodes]
  );

  const visibleOtherRecommendations = useMemo(
    () => OTHER_RECOMMENDATIONS.filter((rec) => !existingCodes.has(rec.code)),
    [existingCodes]
  );

  const missingStandardCount = missingStandardPresets.length;

  const tableRows = useMemo(() => {
    const q = tableSearch.trim().toLowerCase();
    let list = rows.map((r) => {
      const v = allocation.resolved[String(r.code).trim()];
      const pct = Number.isFinite(v) && monthlyCTC > 0 ? (v / monthlyCTC) * 100 : null;
      return { ...r, _value: v, _pct: pct };
    });
    if (q) list = list.filter((r) => `${r.code} ${r.name} ${r.expression}`.toLowerCase().includes(q));
    const dir = sort.dir === "asc" ? 1 : -1;
    list.sort((a, b) => {
      if (sort.key === "value") return ((a._value ?? -1) - (b._value ?? -1)) * dir;
      if (sort.key === "name") return String(a.name || "").localeCompare(String(b.name || "")) * dir;
      return String(a.code || "").localeCompare(String(b.code || "")) * dir;
    });
    return list;
  }, [rows, tableSearch, sort, allocation.resolved, monthlyCTC]);

  function toggleSort(key) {
    setSort((s) => (s.key === key ? { key, dir: s.dir === "asc" ? "desc" : "asc" } : { key, dir: "asc" }));
  }

  function applyValidationResult(res, { isEdit = false } = {}) {
    if (isEdit) {
      setEditValidation(res);
      if (res?.normalized_expression && res.normalized_expression !== editForm.expression.trim()) {
        setEditForm((prev) => ({ ...prev, expression: res.normalized_expression }));
      }
    } else {
      setValidation(res);
      if (res?.normalized_expression && res.normalized_expression !== form.expression.trim()) {
        setForm((prev) => ({ ...prev, expression: res.normalized_expression }));
      }
    }
    if (res && res.is_valid === false) {
      const msg = res.error || "Invalid formula";
      setError(msg);
      toast.error(msg);
      return false;
    }
    setError("");
    if (res?.is_valid) {
      toast.success(res?.warnings?.length ? `Formula valid. ${res.warnings.join(" ")}` : "Formula valid");
    }
    return true;
  }

  async function validateExpression(expression, variableCode, identifiers, { isEdit = false } = {}) {
    const res = await v2ValidateFormula({
      expression: expression.trim(),
      variable_code: variableCode ? String(variableCode).trim().toUpperCase() : undefined,
      known_identifiers: identifiers,
      strict_unknown_identifiers: true,
    });
    return applyValidationResult(res, { isEdit });
  }

  async function onValidate() {
    if (!canValidate) return;
    try {
      setValidating(true);
      setError("");
      await validateExpression(form.expression, form.code, knownIdentifiers);
    } catch (e) {
      setValidation(null);
      setError(toErr(e, "Formula validation failed"));
      toast.error(toErr(e, "Formula validation failed"));
    } finally {
      setValidating(false);
    }
  }

  async function onCreate() {
    if (!canSave) return;
    try {
      setSaving(true);
      setSubmitted(true);
      setError("");
      const validateRes = await v2ValidateFormula({
        expression: form.expression.trim(),
        variable_code: form.code.trim().toUpperCase(),
        known_identifiers: knownIdentifiers,
        strict_unknown_identifiers: true,
      });
      if (!validateRes?.is_valid) {
        applyValidationResult(validateRes);
        return;
      }
      const expression = validateRes.normalized_expression || form.expression.trim();
      await v2CreateDerivedVariable({
        code: form.code.trim().toUpperCase(),
        name: form.name.trim(),
        expression,
        is_active: true,
      });
      setForm(emptyForm());
      setValidation(null);
      setSubmitted(false);
      toast.success("Derived variable created.");
      await load();
    } catch (e) {
      setError(toErr(e, "Failed to create derived variable"));
      toast.error(toErr(e, "Failed to create derived variable"));
    } finally {
      setSaving(false);
    }
  }

  function openEdit(row) {
    setEditing(row);
    setEditForm(rowToForm(row));
    setEditSubmitted(false);
    setEditValidation(null);
    setError("");
  }

  async function onEditValidate() {
    if (!editing || !editForm.expression.trim()) return;
    try {
      setEditValidating(true);
      setError("");
      await validateExpression(editForm.expression, editing.code, editKnownIdentifiers, { isEdit: true });
    } catch (e) {
      setEditValidation(null);
      toast.error(toErr(e, "Formula validation failed"));
    } finally {
      setEditValidating(false);
    }
  }

  async function onEditSave() {
    if (!editing || !canEditSave) return;
    try {
      setEditSaving(true);
      setEditSubmitted(true);
      const validateRes = await v2ValidateFormula({
        expression: editForm.expression.trim(),
        variable_code: editing.code,
        known_identifiers: editKnownIdentifiers,
        strict_unknown_identifiers: true,
      });
      if (!validateRes?.is_valid) {
        applyValidationResult(validateRes, { isEdit: true });
        return;
      }
      const expression = validateRes.normalized_expression || editForm.expression.trim();
      await v2UpdateDerivedVariable(editing.variable_id, {
        name: editForm.name.trim(),
        expression,
        is_active: true,
      });
      setEditing(null);
      setEditValidation(null);
      toast.success("Derived variable updated.");
      await load();
    } catch (e) {
      toast.error(toErr(e, "Failed to update derived variable"));
    } finally {
      setEditSaving(false);
    }
  }

  async function onDeleteConfirm() {
    if (!deleteTarget) return;
    try {
      setDeleteLoading(true);
      await v2UpdateDerivedVariable(deleteTarget.variable_id, { is_active: false });
      setDeleteTarget(null);
      toast.success("Derived variable removed.");
      await load();
    } catch (e) {
      toast.error(toErr(e, "Failed to remove derived variable"));
    } finally {
      setDeleteLoading(false);
    }
  }

  function insertToken(token) {
    setForm((prev) => ({ ...prev, expression: prev.expression ? `${prev.expression.trimEnd()} ${token}` : token }));
  }

  function prefillRecommendation(rec) {
    if (existingCodes.has(rec.code)) {
      toast.error(`${rec.code} already exists.`);
      return;
    }
    setForm({ code: rec.code, name: rec.name, expression: rec.expr });
    setValidation(null);
    setSubmitted(false);
    formRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
  }

  async function createRecommendation(rec, { derivedRows = rows } = {}) {
    const code = String(rec.code || "").trim().toUpperCase();
    if (!code) return { ok: false, reason: "missing_code" };
    if (derivedRows.some((r) => String(r.code || "").trim().toUpperCase() === code)) {
      return { ok: false, reason: "exists" };
    }

    const identifiers = buildKnownIdentifiers({ catalog, components, derivedRows, formCode: code });
    const validateRes = await v2ValidateFormula({
      expression: rec.expr.trim(),
      variable_code: code,
      known_identifiers: identifiers,
      strict_unknown_identifiers: true,
    });
    if (!validateRes?.is_valid) {
      return { ok: false, reason: "invalid", error: validateRes?.error || "Invalid formula" };
    }

    const expression = validateRes.normalized_expression || rec.expr.trim();
    const created = await v2CreateDerivedVariable({
      code,
      name: rec.name.trim(),
      expression,
      is_active: true,
    });
    return { ok: true, created: created || { code, name: rec.name.trim(), expression } };
  }

  async function quickAddRecommendation(rec) {
    if (existingCodes.has(rec.code)) {
      toast.error(`${rec.code} already exists.`);
      return;
    }
    try {
      setAddingPresetCode(rec.code);
      setError("");
      const result = await createRecommendation(rec);
      if (!result.ok) {
        if (result.reason === "invalid") {
          toast.error(result.error || "Add dependencies first — form prefilled instead.");
          prefillRecommendation(rec);
          return;
        }
        if (result.reason === "exists") {
          toast.error(`${rec.code} already exists.`);
          return;
        }
        toast.error("Could not add variable.");
        prefillRecommendation(rec);
        return;
      }
      toast.success(`${rec.name} added.`);
      await load();
    } catch (e) {
      toast.error(toErr(e, `Failed to add ${rec.code}`));
      prefillRecommendation(rec);
    } finally {
      setAddingPresetCode("");
    }
  }

  async function addAllStandardPresets() {
    if (missingStandardCount === 0) {
      toast.error("Standard salary structure is already complete.");
      return;
    }
    try {
      setAddingAllStandard(true);
      setError("");
      let derivedRows = [...rows];
      let added = 0;
      for (const rec of STANDARD_INDIAN_SALARY_PRESETS) {
        const code = String(rec.code || "").trim().toUpperCase();
        if (derivedRows.some((r) => String(r.code || "").trim().toUpperCase() === code)) continue;
        const result = await createRecommendation(rec, { derivedRows });
        if (!result.ok) {
          if (result.reason === "invalid") {
            toast.error(result.error || `Could not add ${rec.code}. Add earlier components first.`);
            prefillRecommendation(rec);
            break;
          }
          continue;
        }
        derivedRows = [...derivedRows, result.created];
        added += 1;
      }
      if (added > 0) {
        toast.success(
          added === missingStandardCount
            ? "Standard Indian salary structure added."
            : `${added} component${added > 1 ? "s" : ""} added.`
        );
        await load();
      }
    } catch (e) {
      toast.error(toErr(e, "Failed to add standard salary structure"));
    } finally {
      setAddingAllStandard(false);
    }
  }

  const tone = allocation.status.tone;

  return (
    <div className="df">
      {/* PayrollComponentToolsBar removed — navigation handled by settings sidebar */}
      {error ? (
        <div className="df-error">
          <span className="min-w-0 flex-1 break-words">{String(error)}</span>
          <button type="button" onClick={load} className="df-error-btn">Retry</button>
        </div>
      ) : null}

      {/* ── TOP KPI DASHBOARD ── */}
      <div className="df-kpis">
        <div className="df-kpi df-kpi-blue">
          <div className="df-kpi-head"><Wallet size={16} /> Monthly CTC</div>
          <input
            className="df-kpi-ctc-input"
            type="number"
            min="0"
            step="1000"
            value={monthlyCTC}
            onChange={(e) => setCTC(e.target.value)}
            aria-label="Sample monthly CTC for preview"
          />
          <div className="df-kpi-sub">Sample value for live preview</div>
        </div>
        <div className="df-kpi df-kpi-green">
          <div className="df-kpi-head"><TrendingUp size={16} /> Allocated</div>
          <div className="df-kpi-value">{money(allocation.allocatedAmount)}</div>
          <div className="df-kpi-sub">{pct1(allocation.allocatedPct)} of CTC</div>
        </div>
        <div className="df-kpi df-kpi-gray">
          <div className="df-kpi-head"><Layers size={16} /> Remaining</div>
          <div className={`df-kpi-value${allocation.remainingAmount < 0 ? " neg" : ""}`}>{money(allocation.remainingAmount)}</div>
          <div className="df-kpi-sub">{pct1(allocation.remainingPct)} of CTC</div>
        </div>
        <div className="df-kpi df-kpi-purple">
          <div className="df-kpi-head"><PieIcon size={16} /> Earnings</div>
          <div className="df-kpi-value">{allocation.segments.length}</div>
          <div className="df-kpi-sub">{rows.length} formulas total</div>
        </div>
      </div>

      {/* ── TWO COLUMN LAYOUT ── */}
      <div className="df-layout">
        <div className="df-main">
          {/* Allocation progress card */}
          <section className="df-card df-alloc">
            <div className="df-alloc-head">
              <div>
                <h2 className="df-alloc-title">Salary Package Allocation</h2>
                <p className="df-alloc-sub">Live preview of how your earning formulas consume the Monthly CTC.</p>
              </div>
              <span className={`df-status df-status-${tone}`}>
                {tone === "red" ? <AlertTriangle size={14} /> : <CheckCircle2 size={14} />}
                {allocation.status.label}
              </span>
            </div>
            <div className="df-alloc-big">{pct1(allocation.allocatedPct)}</div>
            <div className="df-bar">
              <div className={`df-bar-fill df-bar-${tone}`} style={{ width: `${Math.min(100, allocation.allocatedPct)}%` }} />
              {allocation.allocatedPct > 100 ? <div className="df-bar-over" style={{ width: `${Math.min(100, allocation.allocatedPct - 100)}%` }} /> : null}
            </div>
            <div className="df-alloc-foot">
              <span><strong>{money(allocation.allocatedAmount)}</strong> Allocated</span>
              <span className={allocation.remainingAmount < 0 ? "neg" : ""}><strong>{money(allocation.remainingAmount)}</strong> Remaining</span>
            </div>

            {overAllocated ? (
              <div className="df-banner df-banner-red">
                <AlertTriangle size={18} />
                <div>
                  <div className="df-banner-title">Package exceeds Monthly CTC by {pct1(allocation.allocatedPct - 100)}</div>
                  <div className="df-banner-text">Reduce one or more components before publishing. Largest contributors are highlighted below.</div>
                </div>
              </div>
            ) : allocation.remainingPct > 0.5 && allocation.segments.length > 0 ? (
              <div className="df-banner df-banner-green">
                <Info size={18} />
                <div>
                  <div className="df-banner-title">You still have {pct1(allocation.remainingPct)} of Monthly CTC available</div>
                  <div className="df-banner-text">Add an allowance below to complete the package.</div>
                </div>
              </div>
            ) : null}
          </section>

          {/* Distribution + donut */}
          {allocation.segments.length > 0 ? (
            <section className="df-card">
              <div className="df-section-head">
                <h2 className="df-section-title"><PieIcon size={18} className="df-ico-purple" /> Component distribution</h2>
              </div>
              <div className="df-dist-layout">
                <Donut segments={allocation.segments} remainingPct={Math.max(0, allocation.remainingPct)} />
                <div className="df-dist-cards">
                  {allocation.segments.map((s) => {
                    const offending = overAllocated && s.pct >= 25;
                    return (
                      <div className={`df-dist-card${offending ? " offending" : ""}`} key={s.code}>
                        <span className="df-dist-dot" style={{ background: s.color }} />
                        <div className="df-dist-info">
                          <div className="df-dist-name">{s.name} <span className="df-dist-code">{s.code}</span></div>
                          <div className="df-dist-mini"><div className="df-dist-mini-fill" style={{ width: `${Math.min(100, s.pct)}%`, background: s.color }} /></div>
                          <div className="df-dist-label">Consumes {pct1(s.pct)} of Monthly CTC</div>
                        </div>
                        <div className="df-dist-right">
                          <div className="df-dist-pct">{pct1(s.pct)}</div>
                          <div className="df-dist-amt">{money(s.value)}</div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </section>
          ) : null}

          {/* Smart insights */}
          {smartInsights.length > 0 ? (
            <section className="df-card">
              <div className="df-section-head">
                <h2 className="df-section-title"><Sparkles size={18} className="df-ico-purple" /> Smart insights</h2>
              </div>
              <ul className="df-insights">
                {smartInsights.map((it, i) => (
                  <li key={i} className={`df-insight df-insight-${it.tone}`}>
                    {it.tone === "bad" || it.tone === "warn" ? <AlertTriangle size={15} /> : it.tone === "info" ? <Info size={15} /> : <CheckCircle2 size={15} />}
                    <span>{it.text}</span>
                  </li>
                ))}
              </ul>
            </section>
          ) : null}

          {/* Create form */}
          <section className="df-card df-formula" ref={formRef}>
            <div className="df-section-head">
              <h2 className="df-section-title"><Wand2 size={18} className="df-ico-purple" /> Create derived variable</h2>
            </div>
            <p className="df-hint">
              Put the variable name in <strong>Code</strong> only. Use field codes like <span className="df-mono-inline">MONTHLY_CTC * 40%</span> in Expression.
            </p>

            <FormulaFields form={form} setForm={setForm} submitted={submitted} idPrefix="dv-create" />

            {/* Live formula feedback */}
            {form.expression.trim() ? (
              <div className={`df-live${liveFeedback?.ok ? "" : " muted"}`}>
                <Calculator />
                {liveFeedback?.ok ? (
                  <span>
                    Consumes <strong className="df-live-pct">{pct1(liveFeedback.pct)}</strong> of Monthly CTC
                    {" "}(<strong>{money(liveFeedback.value)}</strong>)
                    {liveFeedback.refs.length ? <span className="df-live-ref"> · Derived from {liveFeedback.refs.join(", ")}</span> : null}
                  </span>
                ) : (
                  <span>Live estimate will appear once the expression references known variables (e.g. MONTHLY_CTC).</span>
                )}
              </div>
            ) : null}

            <div className="df-tokens">
              <p className="df-tokens-label">Available field names (click to insert)</p>
              <div className="df-tokens-list">
                {displayTokens.slice(0, 32).map((token) => (
                  <button key={token} type="button" onClick={() => insertToken(token)} className="df-token">{token}</button>
                ))}
              </div>
            </div>

            {validation?.is_valid ? (
              <div className="df-valid">
                <CheckCircle2 size={15} /> <span className="font-semibold">Valid formula</span>
                {validation.normalized_expression ? <code className="df-valid-expr">{validation.normalized_expression}</code> : null}
              </div>
            ) : null}

            <div className="df-actions">
              <button type="button" onClick={onValidate} disabled={!canValidate || validating} className="df-btn df-btn-ghost">
                {validating ? "Validating..." : "Validate formula"}
              </button>
              <button type="button" onClick={onCreate} disabled={!canSave || saving} className="df-btn df-btn-primary">
                <Plus size={15} /> {saving ? "Saving..." : "Create"}
              </button>
            </div>
          </section>

          {/* Existing variables table */}
          <section className="df-card">
            <div className="df-section-head">
              <h2 className="df-section-title"><Layers size={18} className="df-ico-purple" /> Existing derived variables</h2>
              <div className="df-table-tools">
                <div className="df-search">
                  <Search size={14} />
                  <input value={tableSearch} onChange={(e) => setTableSearch(e.target.value)} placeholder="Search…" aria-label="Search variables" />
                </div>
                <button type="button" onClick={load} disabled={loading} className="df-btn df-btn-ghost df-btn-sm">
                  {loading ? "Loading..." : "Refresh"}
                </button>
              </div>
            </div>
            <div className="df-table-wrap">
              <table className="df-table">
                <thead>
                  <tr>
                    <th><button type="button" className="df-th-sort" onClick={() => toggleSort("code")}>Code <ArrowUpDown size={11} /></button></th>
                    <th><button type="button" className="df-th-sort" onClick={() => toggleSort("name")}>Name <ArrowUpDown size={11} /></button></th>
                    <th>Expression</th>
                    <th><button type="button" className="df-th-sort" onClick={() => toggleSort("value")}>Est. amount <ArrowUpDown size={11} /></button></th>
                    <th>% of CTC</th>
                    <th className="text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {tableRows.map((r) => (
                    <tr key={r.variable_id}>
                      <td><span className="df-code-chip">{r.code}</span></td>
                      <td className="df-td-name">{r.name}</td>
                      <td><code className="df-td-expr">{r.expression}</code></td>
                      <td className="df-td-amt">{Number.isFinite(r._value) ? money(r._value) : "—"}</td>
                      <td>
                        {r._pct != null ? (
                          <div className="df-td-pct">
                            <div className="df-td-bar"><div className="df-td-bar-fill" style={{ width: `${Math.min(100, Math.max(0, r._pct))}%` }} /></div>
                            <span>{pct1(r._pct)}</span>
                          </div>
                        ) : <span className="df-muted">—</span>}
                      </td>
                      <td className="text-right">
                        <div className="df-row-actions">
                          {editingComponent ? (
                            <button
                              type="button"
                              onClick={() => applyExpressionForComponent(r.expression)}
                              className="df-act df-act-accent"
                              title="Use for component"
                            >
                              Use
                            </button>
                          ) : null}
                          <button type="button" onClick={() => openEdit(r)} className="df-act" title="Edit" aria-label={`Edit ${r.code}`}><Pencil size={15} /></button>
                          <button type="button" onClick={() => setDeleteTarget(r)} className="df-act df-act-danger" title="Delete" aria-label={`Delete ${r.code}`}><Trash2 size={15} /></button>
                        </div>
                      </td>
                    </tr>
                  ))}
                  {tableRows.length === 0 ? (
                    <tr>
                      <td className="df-table-empty" colSpan={6}>
                        No derived variables yet. Example: Code <strong>MONTHLY_CTC</strong>, expression <span className="df-mono-inline">ANNUAL_CTC / 12</span>.
                      </td>
                    </tr>
                  ) : null}
                </tbody>
              </table>
            </div>
          </section>
        </div>

        {/* ── STICKY SUMMARY ── */}
        <aside className="df-side">
          <div className="df-card df-summary">
            <h2 className="df-summary-title">Package Summary</h2>
            {allocation.segments.length === 0 ? (
              <p className="df-summary-empty">Add earning variables to see the breakdown.</p>
            ) : (
              <ul className="df-summary-list">
                {allocation.segments.map((s) => (
                  <li key={s.code} className="df-summary-row">
                    <span className="df-summary-name"><span className="df-dot" style={{ background: s.color }} /> {s.name}</span>
                    <span className="df-summary-pct">{pct1(s.pct)}</span>
                  </li>
                ))}
              </ul>
            )}
            <div className="df-summary-totals">
              <div className="df-summary-total">
                <span>Allocated</span>
                <strong className={tone === "red" ? "neg" : "pos"}>{pct1(allocation.allocatedPct)}</strong>
              </div>
              <div className="df-summary-total">
                <span>Remaining</span>
                <strong className={allocation.remainingPct < 0 ? "neg" : ""}>{pct1(allocation.remainingPct)}</strong>
              </div>
            </div>
          </div>

          {/* Recommendations — always show standard structure reference */}
          <div className="df-card df-recs">
            <h2 className="df-recs-title"><Lightbulb size={16} /> Suggested allowances</h2>
            <p className="df-recs-sub">Standard Indian CTC formulas are always shown below. Click + to add missing ones.</p>

            {missingStandardCount > 0 ? (
              <button
                type="button"
                className="df-btn df-btn-primary df-recs-all"
                onClick={addAllStandardPresets}
                disabled={addingAllStandard || Boolean(addingPresetCode)}
              >
                <Sparkles size={14} />
                {addingAllStandard ? "Adding structure…" : `Add all ${missingStandardCount} missing standard components`}
              </button>
            ) : (
              <div className="df-recs-done">
                <CheckCircle2 size={15} /> All {STANDARD_INDIAN_SALARY_PRESETS.length} standard components are added.
              </div>
            )}

            <div className="df-recs-list">
              {STANDARD_INDIAN_SALARY_PRESETS.map((rec) => {
                const added = existingCodes.has(rec.code);
                const row = rowsByCode.get(rec.code);
                const expr = row?.expression || rec.expr;
                if (added) {
                  return (
                    <div key={rec.code} className="df-rec df-rec-standard df-rec-added">
                      <CheckCircle2 size={14} />
                      <span className="df-rec-text">
                        <span className="df-rec-name">
                          {rec.name}
                          <span className="df-rec-badge">Standard</span>
                        </span>
                        <span className="df-rec-note">{rec.note}</span>
                        <code className="df-rec-expr">{expr}</code>
                      </span>
                      <span className="df-rec-added-badge">Added</span>
                    </div>
                  );
                }
                return (
                  <button
                    type="button"
                    key={rec.code}
                    className="df-rec df-rec-standard"
                    onClick={() => quickAddRecommendation(rec)}
                    disabled={addingAllStandard || (addingPresetCode && addingPresetCode !== rec.code)}
                    title={rec.expr}
                  >
                    <Plus size={14} />
                    <span className="df-rec-text">
                      <span className="df-rec-name">
                        {rec.name}
                        <span className="df-rec-badge">Standard</span>
                      </span>
                      <span className="df-rec-note">{rec.note}</span>
                      <code className="df-rec-expr">{rec.expr}</code>
                    </span>
                    {addingPresetCode === rec.code ? <span className="df-rec-busy">Adding…</span> : null}
                  </button>
                );
              })}
            </div>

            {visibleOtherRecommendations.length > 0 ? (
              <>
                <div className="df-recs-divider">Other allowances</div>
                <div className="df-recs-list">
                  {visibleOtherRecommendations.map((rec) => (
                    <button
                      type="button"
                      key={rec.code}
                      className="df-rec"
                      onClick={() => quickAddRecommendation(rec)}
                      disabled={addingAllStandard || Boolean(addingPresetCode)}
                      title={rec.expr}
                    >
                      <Plus size={14} />
                      <span className="df-rec-text">
                        <span className="df-rec-name">{rec.name}</span>
                        <span className="df-rec-note">{rec.note}</span>
                      </span>
                    </button>
                  ))}
                </div>
              </>
            ) : null}
          </div>
        </aside>
      </div>

      {/* Edit modal (unchanged logic) */}
      {editing
        ? createPortal(
            <div className="df-modal-overlay" role="dialog" aria-modal="true" aria-labelledby="edit-derived-variable-title" onClick={() => !editSaving && setEditing(null)}>
              <div className="df-modal" onClick={(e) => e.stopPropagation()}>
                <h2 id="edit-derived-variable-title" className="df-modal-title">Edit derived variable — {editing.code}</h2>
                <p className="df-modal-sub">Update the display name or formula. Code cannot be changed after creation.</p>
                <FormulaFields form={editForm} setForm={setEditForm} submitted={editSubmitted} idPrefix="dv-edit" codeReadOnly />
                {editValidation?.is_valid ? (
                  <div className="df-valid" style={{ marginTop: 12 }}>
                    <CheckCircle2 size={15} /> <span className="font-semibold">Valid formula</span>
                    {editValidation.normalized_expression ? <code className="df-valid-expr">{editValidation.normalized_expression}</code> : null}
                  </div>
                ) : null}
                <div className="df-modal-foot">
                  <button type="button" className="df-btn df-btn-ghost" onClick={onEditValidate} disabled={!editForm.expression.trim() || editValidating || editSaving}>
                    {editValidating ? "Validating..." : "Validate formula"}
                  </button>
                  <button type="button" className="df-btn df-btn-ghost" onClick={() => setEditing(null)} disabled={editSaving}>Cancel</button>
                  <button type="button" className="df-btn df-btn-primary" onClick={onEditSave} disabled={!canEditSave || editSaving}>
                    {editSaving ? "Saving..." : "Save changes"}
                  </button>
                </div>
              </div>
            </div>,
            document.body
          )
        : null}

      <ConfirmDialog
        open={Boolean(deleteTarget)}
        title="Delete this derived variable?"
        message={deleteTarget ? `"${deleteTarget.code}" will be removed from the list. Templates or formulas that reference it may need to be updated.` : ""}
        confirmLabel="Delete"
        cancelLabel="Cancel"
        danger
        loading={deleteLoading}
        onCancel={() => !deleteLoading && setDeleteTarget(null)}
        onConfirm={onDeleteConfirm}
      />
    </div>
  );
}
